import { useState } from "react";
import { Link } from "react-router-dom";
import { MdOutlineQuestionAnswer } from "react-icons/md";
import {
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";

const TIPOS = [
  "Pedido de acesso à informação",
  "Recurso",
  "Reclamação",
  "Desclassificação de informação sigilosa",
  "Consulta",
];

const SETORES = [
  "Secretaria de Administração",
  "Secretaria de Finanças",
  "Secretaria de Saúde",
  "Secretaria de Educação",
  "Secretaria de Agricultura",
  "Secretaria de Assistência Social",
  "Controladoria",
  "Gabinete do Prefeito",
  "Não sei / Outro",
];

const EMPTY = {
  nome: "",
  cpf: "",
  email: "",
  telefone: "",
  tipo: "",
  setor: "",
  assunto: "",
  descricao: "",
  sigilo: false,
  termos: false,
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Field = ({ label, error, required, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
    {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
  </div>
);

const inputCls = (error) =>
  `w-full border rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${
    error ? "border-red-400 bg-red-50" : "border-gray-300 bg-white"
  }`;

const generateProtocol = () => {
  const year = new Date().getFullYear();
  const num = String(Math.floor(10000 + Math.random() * 90000));
  return `eSIC-${year}-${num}`;
};

const ESIC = () => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [protocol, setProtocol] = useState("");

  const set = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.nome.trim() || form.nome.trim().length < 3)
      errs.nome = "Informe o nome completo (mínimo 3 caracteres).";
    if (!form.email.trim()) errs.email = "E-mail é obrigatório.";
    else if (!emailRegex.test(form.email)) errs.email = "Informe um e-mail válido.";
    if (!form.tipo) errs.tipo = "Selecione o tipo de solicitação.";
    if (!form.assunto.trim() || form.assunto.trim().length < 5)
      errs.assunto = "Informe o assunto (mínimo 5 caracteres).";
    if (!form.descricao.trim() || form.descricao.trim().length < 20)
      errs.descricao = "Descreva o pedido com pelo menos 20 caracteres.";
    if (!form.termos) errs.termos = "É necessário declarar a veracidade das informações.";
    return errs;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setProtocol(generateProtocol());
    setSubmitted(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (submitted) {
    return (
      <div className="p-6 md:p-8 max-w-2xl mx-auto space-y-6">
        <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center space-y-4">
          <CheckCircleIcon className="h-14 w-14 text-green-600 mx-auto" />
          <div>
            <h2 className="text-xl font-bold text-green-900">Solicitação Enviada!</h2>
            <p className="text-sm text-green-800 mt-1">
              Sua solicitação de acesso à informação foi registrada com sucesso.
            </p>
          </div>
          <div className="bg-white border border-green-300 rounded-xl p-4 inline-block">
            <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Número de Protocolo</p>
            <p className="text-2xl font-bold text-blue-950 mt-1 tracking-widest">{protocol}</p>
          </div>
          <div className="text-sm text-green-800 bg-green-100 rounded-xl p-4 text-left space-y-1">
            <p><strong>Próximos passos:</strong></p>
            <p>• Guarde o número de protocolo para acompanhar sua solicitação.</p>
            <p>• Você receberá a resposta no e-mail informado em até <strong>20 dias corridos</strong>.</p>
            <p>• Em caso de dúvidas, entre em contato pelo telefone <strong>(84) 99999-9999</strong>.</p>
          </div>
        </div>
        <button
          onClick={() => { setSubmitted(false); setForm(EMPTY); setErrors({}); }}
          className="inline-flex items-center gap-2 text-sm text-blue-700 hover:underline"
        >
          <ArrowLeftIcon className="h-4 w-4" /> Fazer nova solicitação
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-4xl mx-auto">

      {/* Header */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-5 flex items-start gap-4">
        <span className="text-blue-700 text-3xl mt-0.5 shrink-0">
          <MdOutlineQuestionAnswer />
        </span>
        <div>
          <h2 className="text-xl font-bold text-blue-950 mb-1">
            e-SIC — Serviço de Informação ao Cidadão Eletrônico
          </h2>
          <p className="text-blue-900 text-sm leading-relaxed">
            Envie sua solicitação de acesso à informação de forma online. O pedido será registrado
            e você receberá um número de protocolo para acompanhamento. Fundamentado na{" "}
            <strong>Lei nº 12.527/2011 (Lei de Acesso à Informação)</strong>.
          </p>
        </div>
      </div>

      {/* Error summary */}
      {Object.keys(errors).length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-sm font-semibold text-red-800 mb-1">Corrija os erros abaixo antes de enviar:</p>
          <ul className="text-sm text-red-700 list-disc list-inside space-y-0.5">
            {Object.values(errors).map((msg, i) => (
              <li key={i}>{msg}</li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate className="space-y-6">

        {/* Section 1: Identification */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
          <h3 className="text-base font-bold text-blue-950 flex items-center gap-2 pb-2 border-b border-gray-100">
            <ClipboardDocumentCheckIcon className="h-5 w-5 text-blue-700" />
            Identificação do Solicitante
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Nome completo" required error={errors.nome}>
              <input
                type="text"
                value={form.nome}
                onChange={set("nome")}
                placeholder="Seu nome completo"
                className={inputCls(errors.nome)}
              />
            </Field>
            <Field label="CPF" error={errors.cpf}>
              <input
                type="text"
                value={form.cpf}
                onChange={set("cpf")}
                placeholder="000.000.000-00 (opcional)"
                maxLength={14}
                className={inputCls(errors.cpf)}
              />
            </Field>
            <Field label="E-mail" required error={errors.email}>
              <input
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="seu@email.com.br"
                className={inputCls(errors.email)}
              />
            </Field>
            <Field label="Telefone" error={errors.telefone}>
              <input
                type="tel"
                value={form.telefone}
                onChange={set("telefone")}
                placeholder="(84) 99999-9999 (opcional)"
                className={inputCls(errors.telefone)}
              />
            </Field>
          </div>
        </div>

        {/* Section 2: Request */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
          <h3 className="text-base font-bold text-blue-950 flex items-center gap-2 pb-2 border-b border-gray-100">
            <MdOutlineQuestionAnswer className="text-xl text-blue-700" />
            Dados da Solicitação
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Tipo de solicitação" required error={errors.tipo}>
              <select
                value={form.tipo}
                onChange={set("tipo")}
                className={inputCls(errors.tipo)}
              >
                <option value="">Selecione...</option>
                {TIPOS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </Field>
            <Field label="Órgão / Setor" error={errors.setor}>
              <select
                value={form.setor}
                onChange={set("setor")}
                className={inputCls(errors.setor)}
              >
                <option value="">Selecione (opcional)...</option>
                {SETORES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="Assunto" required error={errors.assunto}>
            <input
              type="text"
              value={form.assunto}
              onChange={set("assunto")}
              placeholder="Resumo da informação solicitada"
              maxLength={100}
              className={inputCls(errors.assunto)}
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{form.assunto.length}/100</p>
          </Field>
          <Field label="Descrição do pedido" required error={errors.descricao}>
            <textarea
              value={form.descricao}
              onChange={set("descricao")}
              placeholder="Descreva com detalhes a informação que você deseja acessar..."
              rows={5}
              maxLength={4000}
              className={`${inputCls(errors.descricao)} resize-none`}
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{form.descricao.length}/4000</p>
          </Field>
        </div>

        {/* Section 3: Options & Terms */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
          <h3 className="text-base font-bold text-blue-950 pb-2 border-b border-gray-100">
            Opções e Declaração
          </h3>
          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={form.sigilo}
              onChange={set("sigilo")}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <div>
              <p className="text-sm font-medium text-gray-700 group-hover:text-blue-800">
                Manter sigilo do solicitante
              </p>
              <p className="text-xs text-gray-400">
                Seus dados pessoais não serão divulgados na resposta ou em publicações relacionadas ao pedido.
              </p>
            </div>
          </label>
          <label className={`flex items-start gap-3 cursor-pointer group p-3 rounded-xl border ${errors.termos ? "border-red-300 bg-red-50" : "border-gray-100 bg-gray-50"}`}>
            <input
              type="checkbox"
              checked={form.termos}
              onChange={set("termos")}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <div>
              <p className="text-sm font-medium text-gray-700 group-hover:text-blue-800">
                Declaro que as informações prestadas são verdadeiras <span className="text-red-500">*</span>
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                Conforme a Lei nº 12.527/2011, a prestação de informações falsas pode implicar responsabilização.
              </p>
            </div>
          </label>
          {errors.termos && <p className="text-xs text-red-600">{errors.termos}</p>}
        </div>

        {/* Submit */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-xs text-gray-400">
            Campos marcados com <span className="text-red-500 font-bold">*</span> são obrigatórios.
            O atendimento é gratuito, conforme a Lei de Acesso à Informação.
          </p>
          <button
            type="submit"
            className="shrink-0 bg-blue-900 text-white font-semibold py-3 px-8 rounded-xl hover:bg-blue-700 active:bg-blue-950 transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Enviar Solicitação
          </button>
        </div>

      </form>

      {/* Footer note */}
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-600 flex items-start gap-3">
        <span className="shrink-0 mt-0.5">📌</span>
        <p>
          Prefere o atendimento presencial? Visite o{" "}
          <Link to="/transparencia/sic" className="text-blue-700 hover:underline font-medium">
            SIC Presencial
          </Link>{" "}
          na Rua Manoel Joaquim de Souza, 150, Centro — Serrinha/RN, de segunda a sexta-feira, das 08h às 13h.
        </p>
      </div>

    </div>
  );
};

export default ESIC;
